import { defineStore } from 'pinia';

import { Analysis, AnalysisStatus, BulkApproveFailure, BulkApproveFailureReason } from '@/Analysis/analysis.entity';
import AnalysisService from '@/Analysis/analysis.service';
import { Member } from '@/Member/member.entity';

export const BULK_APPROVE_PAGE_SIZE = 20;

export type BulkApproveAnalysis = {
    analysisId: string;
    fileName: string;
    owner: Member;
    updatedAt: string;
};

// One row of the post-submission result view. Every submitted analysis becomes a row,
// tagged with whether it was approved and, if not, why.
export type BulkApproveResultRow = BulkApproveAnalysis & {
    approved: boolean;
    reason?: BulkApproveFailureReason;
};

type BulkApproveState = {
    analyses: BulkApproveAnalysis[];
    // Cache of every analysis seen across pages, keyed by id. Selection can span pages,
    // so once a submission returns we need the file name / owner of analyses that are no
    // longer on the current page to render the result view.
    analysesById: Record<string, BulkApproveAnalysis>;
    total: number;
    currentPage: number;
    loading: boolean;
    error: boolean;
    selectedIds: string[];
    // The exact set of ids sent to the server, preserved so the result view can list every
    // submitted analysis (approved and failed alike) regardless of the current page.
    submittedIds: string[];
    submitting: boolean;
    submitted: boolean;
    approvedCount: number;
    failures: BulkApproveFailure[];
    submitError: boolean;
};

export const useBulkApproveStore = defineStore('bulkApprove', {
    state: (): BulkApproveState => ({
        analyses: [],
        analysesById: {},
        total: 0,
        currentPage: 1,
        loading: false,
        error: false,
        selectedIds: [],
        submittedIds: [],
        submitting: false,
        submitted: false,
        approvedCount: 0,
        failures: [],
        submitError: false,
    }),
    getters: {
        selectedCount(state: BulkApproveState): number {
            return state.selectedIds.length;
        },
        isPartialFailure(state: BulkApproveState): boolean {
            return state.failures.length > 0;
        },
        failedCount(state: BulkApproveState): number {
            return state.failures.length;
        },
        // The currently selected analyses (across every page), resolved from the page cache
        // so the confirmation step can review exactly what is about to be approved.
        selectedAnalyses(state: BulkApproveState): BulkApproveAnalysis[] {
            return state.selectedIds
                .map((analysisId) => state.analysesById[analysisId])
                .filter((analysis): analysis is BulkApproveAnalysis => Boolean(analysis));
        },
        // Every submitted analysis as a result row, failures first so the actionable items
        // sit at the top of the list. Reads the cached details captured while browsing pages.
        resultRows(state: BulkApproveState): BulkApproveResultRow[] {
            const reasonById = new Map(state.failures.map((failure) => [failure.analysisId, failure.reason]));

            return state.submittedIds
                .map((analysisId) => {
                    const analysis = state.analysesById[analysisId];
                    return {
                        analysisId,
                        fileName: analysis?.fileName ?? '',
                        owner: analysis?.owner,
                        updatedAt: analysis?.updatedAt ?? '',
                        reason: reasonById.get(analysisId),
                        approved: !reasonById.has(analysisId),
                    };
                })
                .sort((first, second) => Number(first.approved) - Number(second.approved));
        },
    },
    actions: {
        async fetchPage(litigationId: string, page: number): Promise<void> {
            this.loading = true;
            this.error = false;
            try {
                const analysisService = new AnalysisService();
                const response = await analysisService.findAll(
                    page,
                    '',
                    { status: [AnalysisStatus.CHECKING], litigation: [litigationId] },
                    '-updatedAt',
                );
                this.total = Number(response.meta.count);
                this.currentPage = page;
                this.analyses = response.data.map((analysis: Analysis) => ({
                    analysisId: analysis.analysisId,
                    fileName: analysis.decision?.fileName || '',
                    owner: analysis.owner,
                    updatedAt: analysis.updatedAt,
                }));
                this.analyses.forEach((analysis) => {
                    this.analysesById[analysis.analysisId] = analysis;
                });
            } catch (error) {
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        async submit(): Promise<void> {
            const submittedIds = [...this.selectedIds];
            this.submitting = true;
            this.submitError = false;
            this.failures = [];
            this.submittedIds = submittedIds;
            try {
                const analysisService = new AnalysisService();
                const result = await analysisService.bulkApprove(submittedIds);
                this.approvedCount = result.approved.length;
                this.failures = result.failed;
                // `submitted` marks a completed submission (full or partial); `isPartialFailure`
                // distinguishes the two. The result view then lists every submitted analysis.
                this.submitted = true;
            } catch (error) {
                // Total server/network failure: restore the previous selection so the user can retry.
                this.submitError = true;
                this.submittedIds = [];
                this.selectedIds = submittedIds;
            } finally {
                this.submitting = false;
            }
        },
        isSelected(id: string): boolean {
            return this.selectedIds.includes(id);
        },
        toggleOne(id: string): void {
            if (this.selectedIds.includes(id)) {
                this.selectedIds = this.selectedIds.filter((selectedId) => selectedId !== id);
            } else {
                this.selectedIds.push(id);
            }
        },
        setPageSelection(ids: string[], checked: boolean): void {
            if (checked) {
                const merged = new Set([...this.selectedIds, ...ids]);
                this.selectedIds = [...merged];
            } else {
                this.selectedIds = this.selectedIds.filter((selectedId) => !ids.includes(selectedId));
            }
        },
        clear(): void {
            this.$reset();
        },
    },
});
